import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  MdSchool,
  MdSave,
  MdArrowBack,
  MdDelete,
  MdImage,
  MdAdd,
} from "react-icons/md";

// Keep the toolbar focused — headings, basic formatting, lists and
// links cover what a college description needs without bloating the UI.
const descriptionModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

// ---------------------------------------
// Helpers: nested cutoffRanks <-> flat rows
// Backend shape (see attachChildren / nestCutoffs):
// [{ year, quota, categories: [{ name, rounds: [{round, rank}] }] }]
// ---------------------------------------

const makeRowId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function flattenCutoffs(nested) {
  const rows = [];
  (nested || []).forEach((q) => {
    (q.categories || []).forEach((cat) => {
      (cat.rounds || []).forEach((r) => {
        rows.push({
          id: makeRowId(),
          year: q.year ?? "",
          quota: q.quota ?? "",
          category: cat.name ?? "",
          round: r.round ?? "",
          rank: r.rank ?? "",
        });
      });
    });
  });
  return rows;
}

function nestCutoffRows(rows) {
  const map = new Map();

  rows.forEach((r) => {
    if (!r.year || !r.category || !r.round || !r.rank) return; // skip incomplete rows

    const key = `${r.year}|${r.quota || ""}`;
    if (!map.has(key)) {
      map.set(key, {
        year: Number(r.year),
        quota: r.quota || null,
        categories: new Map(),
      });
    }

    const q = map.get(key);
    if (!q.categories.has(r.category)) {
      q.categories.set(r.category, { name: r.category, rounds: [] });
    }

    q.categories.get(r.category).rounds.push({
      round: Number(r.round),
      rank: Number(r.rank),
    });
  });

  return [...map.values()].map((q) => ({
    year: q.year,
    quota: q.quota,
    categories: [...q.categories.values()],
  }));
}

const EditCollege = () => {
  const { collegeId } = useParams();
  const navigate = useNavigate();

  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fields match the columns the backend's updateCollegeNew handler
  // actually reads from req.body (see the `map` object in college.js).
  const [data, setData] = useState({
    college_code: "",
    college_name: "",
    management_text: "",
    university: "",
    year_of_inc: "",
    mbbs_intake: "",
    nmc_status: "",
    description: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    website: "",
    email: "",
    telephone: "",
    location: "",
    google_maps_link: "",
    college_ranking: "",
    admission_process: "",
    country_priority: "",
    state_priority: "",
  });

  // Backend upload middleware only accepts img1 & img2
  // (upload.fields([{ name: "img1" }, { name: "img2" }]))
  const [images, setImages] = useState({
    img1: { file: null, preview: null, oldUrl: null },
    img2: { file: null, preview: null, oldUrl: null },
  });

  const [courses, setCourses] = useState([]);
  const [cutoffRows, setCutoffRows] = useState([]);

  // ---------------------------------------
  // Get Single College — GET /api/v1/colleges/new/:id
  // ---------------------------------------

  const getSingleCollege = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${base_url}/api/v1/colleges/new/${collegeId}`
      );

      const college = response.data.data;

      setData({
        college_code: college.college_code || "",
        college_name: college.college_name || "",
        management_text: college.management_text || "",
        university: college.university || "",
        year_of_inc: college.year_of_inc || "",
        mbbs_intake: college.mbbs_intake ?? "",
        nmc_status: college.nmc_status || "",
        description: college.description || "",
        address: college.address || "",
        city: college.city || "",
        state: college.state || "",
        pincode: college.pincode || "",
        website: college.website || "",
        email: college.email || "",
        telephone: college.telephone || "",
        location: college.location || "",
        google_maps_link: college.google_maps_link || "",
        college_ranking: college.college_ranking || "",
        admission_process: college.admission_process || "",
        country_priority: college.country_priority ?? "",
        state_priority: college.state_priority ?? "",
      });

      setImages({
        img1: { file: null, preview: null, oldUrl: college.img1 || null },
        img2: { file: null, preview: null, oldUrl: college.img2 || null },
      });

      setCourses(
        (college.courses || []).map((c) => ({
          id: c.id ?? makeRowId(),
          course: c.course || "",
          intake: c.intake ?? "",
        }))
      );

      setCutoffRows(flattenCutoffs(college.cutoffRanks));
    } catch (error) {
      console.error("College Fetch Error:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch college");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (collegeId) getSingleCollege();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collegeId]);

  // ---------------------------------------
  // Input Handler
  // ---------------------------------------

  const formHandler = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  // ---------------------------------------
  // Image Handler
  // ---------------------------------------

  const imageHandler = (e, imageName) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    const preview = URL.createObjectURL(file);
    setImages((prev) => ({
      ...prev,
      [imageName]: { ...prev[imageName], file, preview },
    }));
  };

  const removeSelectedImage = (imageName) => {
    setImages((prev) => ({
      ...prev,
      [imageName]: { ...prev[imageName], file: null, preview: null },
    }));
  };

  // ---------------------------------------
  // Courses Handlers
  // ---------------------------------------

  const addCourseRow = () => {
    setCourses((prev) => [
      ...prev,
      { id: makeRowId(), course: "", intake: "" },
    ]);
  };

  const updateCourseRow = (id, field, value) => {
    setCourses((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const removeCourseRow = (id) => {
    setCourses((prev) => prev.filter((c) => c.id !== id));
  };

  // ---------------------------------------
  // Cutoff Rank Handlers
  // ---------------------------------------

  const addCutoffRow = () => {
    setCutoffRows((prev) => [
      ...prev,
      {
        id: makeRowId(),
        year: new Date().getFullYear(),
        quota: "",
        category: "",
        round: "",
        rank: "",
      },
    ]);
  };

  const updateCutoffRow = (id, field, value) => {
    setCutoffRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const removeCutoffRow = (id) => {
    setCutoffRows((prev) => prev.filter((r) => r.id !== id));
  };

  // ---------------------------------------
  // Submit — PUT /api/v1/colleges/new/:collegeId
  // ---------------------------------------

  const btnHandler = async (e) => {
    e.preventDefault();

    if (!data.college_name.trim()) {
      toast.error("College name is required");
      return;
    }

    if (!data.college_code.trim()) {
      toast.error("College code is required");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      // Only these keys are read by updateCollegeNew's field map,
      // so we send exactly these snake_case names.
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value ?? "");
      });

      // Courses + cutoffRanks are sent as JSON strings and parsed
      // server-side with parseMaybeJson, then fully replace the
      // existing rows for this college (replaceCourses / replaceCutoffs).
      const cleanCourses = courses
        .filter((c) => c.course.trim())
        .map((c) => ({
          course: c.course.trim(),
          intake: c.intake === "" ? null : Number(c.intake),
        }));
      formData.append("courses", JSON.stringify(cleanCourses));

      const nestedCutoffs = nestCutoffRows(cutoffRows);
      formData.append("cutoffRanks", JSON.stringify(nestedCutoffs));

      // Images — only send newly selected files
      if (images.img1.file) formData.append("img1", images.img1.file);
      if (images.img2.file) formData.append("img2", images.img2.file);

      const response = await axios.put(
        `${base_url}/api/v1/colleges/new/${collegeId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      toast.success(response.data.message || "College updated successfully");

      setTimeout(() => {
        navigate("/dashboard/mbbs/college-list");
      }, 1000);
    } catch (error) {
      console.error("Update College Error:", error);
      toast.error(error?.response?.data?.message || "Failed to update college");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------
  // Image Preview Component
  // ---------------------------------------

  const ImageBox = ({ imageName, label }) => {
    const image = images[imageName];
    const displayImage = image.preview || image.oldUrl;

    return (
      <div className="border rounded p-3 h-100">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="fw-bold mb-0">
            <MdImage className="me-1" />
            {label}
          </label>

          {image.preview && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => removeSelectedImage(imageName)}
            >
              <MdDelete />
            </button>
          )}
        </div>

        <div
          className="bg-light rounded d-flex align-items-center justify-content-center mb-3"
          style={{ height: "180px", overflow: "hidden" }}
        >
          {displayImage ? (
            <img
              src={displayImage}
              alt={label}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div className="text-muted text-center">
              <MdImage size={40} />
              <div>No Image</div>
            </div>
          )}
        </div>

        <input
          type="file"
          className="form-control"
          accept="image/*"
          onChange={(e) => imageHandler(e, imageName)}
        />

        {image.preview && (
          <small className="text-success d-block mt-2">
            New image selected
          </small>
        )}
      </div>
    );
  };

  // ---------------------------------------
  // Loading
  // ---------------------------------------

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <div className="mt-3">Loading college details...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------
  // UI
  // ---------------------------------------

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        {/* Header */}
        <div className="card shadow-sm border-0 mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="fw-bold mb-1">
                  <MdSchool className="me-2 text-primary" />
                  Edit College
                </h4>
                <small className="text-muted">College ID: {collegeId}</small>
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate("/dashboard/mbbs/college-list")}
              >
                <MdArrowBack className="me-1" />
                Back
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={btnHandler}>
          {/* Basic Information */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-white">
              <h5 className="fw-bold mb-0">Basic Information</h5>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    College Code *
                  </label>
                  <input
                    type="text"
                    name="college_code"
                    className="form-control"
                    value={data.college_code}
                    onChange={formHandler}
                    required
                  />
                </div>

                <div className="col-md-9">
                  <label className="form-label fw-semibold">
                    College Name *
                  </label>
                  <input
                    type="text"
                    name="college_name"
                    className="form-control"
                    value={data.college_name}
                    onChange={formHandler}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">
                    Management Text
                  </label>
                  <input
                    type="text"
                    name="management_text"
                    className="form-control"
                    placeholder="e.g. Government / Private / Trust"
                    value={data.management_text}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">University</label>
                  <input
                    type="text"
                    name="university"
                    className="form-control"
                    value={data.university}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label fw-semibold">
                    Year Established
                  </label>
                  <input
                    type="text"
                    name="year_of_inc"
                    className="form-control"
                    value={data.year_of_inc}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-2">
                  <label className="form-label fw-semibold">
                    MBBS Intake
                  </label>
                  <input
                    type="number"
                    name="mbbs_intake"
                    className="form-control"
                    value={data.mbbs_intake}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    NMC Status
                  </label>
                  <input
                    type="text"
                    name="nmc_status"
                    className="form-control"
                    value={data.nmc_status}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    College Ranking
                  </label>
                  <input
                    type="text"
                    name="college_ranking"
                    className="form-control"
                    value={data.college_ranking}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    Country Priority
                  </label>
                  <input
                    type="number"
                    name="country_priority"
                    className="form-control"
                    value={data.country_priority}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">
                    State Priority
                  </label>
                  <input
                    type="number"
                    name="state_priority"
                    className="form-control"
                    value={data.state_priority}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label fw-semibold">
                    Admission Process
                  </label>
                  <textarea
                    name="admission_process"
                    className="form-control"
                    rows="2"
                    value={data.admission_process}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label fw-semibold">
                    Description
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={data.description}
                    onChange={(html) =>
                      setData((prev) => ({ ...prev, description: html }))
                    }
                    modules={descriptionModules}
                    className="bg-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contact & Location */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-white">
              <h5 className="fw-bold mb-0">Contact & Location</h5>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-semibold">City</label>
                  <input
                    type="text"
                    name="city"
                    className="form-control"
                    value={data.city}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">State</label>
                  <input
                    type="text"
                    name="state"
                    className="form-control"
                    value={data.state}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    className="form-control"
                    value={data.pincode}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={data.email}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Telephone</label>
                  <input
                    type="text"
                    name="telephone"
                    className="form-control"
                    value={data.telephone}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Website</label>
                  <input
                    type="text"
                    name="website"
                    className="form-control"
                    placeholder="https://example.com"
                    value={data.website}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label fw-semibold">Address</label>
                  <textarea
                    name="address"
                    className="form-control"
                    rows="3"
                    value={data.address}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Location (short label / area)
                  </label>
                  <input
                    type="text"
                    name="location"
                    className="form-control"
                    value={data.location}
                    onChange={formHandler}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Google Maps Link
                  </label>
                  <input
                    type="text"
                    name="google_maps_link"
                    className="form-control"
                    value={data.google_maps_link}
                    onChange={formHandler}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Courses */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0">Courses</h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={addCourseRow}
              >
                <MdAdd className="me-1" />
                Add Course
              </button>
            </div>

            <div className="card-body">
              {courses.length === 0 ? (
                <p className="text-muted mb-0">
                  No courses added yet. Click "Add Course" to add one.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Course Name</th>
                        <th style={{ width: "160px" }}>Intake</th>
                        <th style={{ width: "60px" }} />
                      </tr>
                    </thead>
                    <tbody>
                      {courses.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. MBBS"
                              value={c.course}
                              onChange={(e) =>
                                updateCourseRow(c.id, "course", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={c.intake}
                              onChange={(e) =>
                                updateCourseRow(c.id, "intake", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeCourseRow(c.id)}
                            >
                              <MdDelete />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Cutoff Ranks */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0">Cutoff Ranks</h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={addCutoffRow}
              >
                <MdAdd className="me-1" />
                Add Row
              </button>
            </div>

            <div className="card-body">
              {cutoffRows.length === 0 ? (
                <p className="text-muted mb-0">
                  No cutoff ranks added yet. Click "Add Row" to add one.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: "110px" }}>Year</th>
                        <th style={{ width: "140px" }}>Quota</th>
                        <th>Category</th>
                        <th style={{ width: "110px" }}>Round</th>
                        <th style={{ width: "130px" }}>Rank</th>
                        <th style={{ width: "60px" }} />
                      </tr>
                    </thead>
                    <tbody>
                      {cutoffRows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={r.year}
                              onChange={(e) =>
                                updateCutoffRow(r.id, "year", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. State / AIQ"
                              value={r.quota}
                              onChange={(e) =>
                                updateCutoffRow(r.id, "quota", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. General / OBC"
                              value={r.category}
                              onChange={(e) =>
                                updateCutoffRow(
                                  r.id,
                                  "category",
                                  e.target.value
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={r.round}
                              onChange={(e) =>
                                updateCutoffRow(r.id, "round", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={r.rank}
                              onChange={(e) =>
                                updateCutoffRow(r.id, "rank", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeCutoffRow(r.id)}
                            >
                              <MdDelete />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <small className="text-muted">
                    Rows with the same Year + Quota + Category are grouped
                    together automatically when saved.
                  </small>
                </div>
              )}
            </div>
          </div>

          {/* Images */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-white">
              <h5 className="fw-bold mb-1">College Images</h5>
              <small className="text-muted">
                Upload up to 2 images. Existing images will remain unchanged
                if no new image is selected.
              </small>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <ImageBox imageName="img1" label="Image 1" />
                </div>
                <div className="col-md-6">
                  <ImageBox imageName="img2" label="Image 2" />
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={saving}
                  onClick={() => navigate("/dashboard/mbbs/college-list")}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <MdSave size={20} className="me-1" />
                      Update College
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default EditCollege;